import type {TurnPermissionsPatch} from '@wandb/weave/lib/WbAgent/WbAgentClient';
import {useEffect, useRef} from 'react';

import {
  builtPromptFromStoreContent,
  resolveTurnParent,
} from '../promptAssembly';
import type {AgentChatStore} from '../store/context';
import type {Thread} from '../store/thread';
import {isAgentChatReadonly} from '../useAgentChatReadonly';
import type {AppendAndRunTurn} from '../useAgentChatRuntime';
import {computeNextAction} from './action';
import {queueKey, useQueueStore} from './store';

/**
 * useQueueDrainer drives every thread's queue from one subscriber, iterating
 * all threads (active or backgrounded) and sending each eligible queue head
 * via `appendAndRunTurn`.
 */
export function useQueueDrainer(
  store: AgentChatStore,
  appendAndRunTurn: AppendAndRunTurn
): void {
  const lastRunIdsRef = useRef(new Map<string, string>());
  const lastHandledRunIdsRef = useRef(new Map<string, string>());

  useEffect(() => {
    const lastRunIds = lastRunIdsRef.current;
    const lastHandledRunIds = lastHandledRunIdsRef.current;

    // initiateDrain sends key's queue head as a new turn.
    const initiateDrain = (thread: Thread, key: string, headId: string) => {
      const queueState = useQueueStore.getState();
      const head = queueState.queues[key]?.find(m => m.id === headId);
      if (!head) {
        return;
      }

      // NOTE: Mark draining before consuming any flag below, so a re-entrant
      // `evaluate` triggered by that write sees the head already draining.
      queueState.markDraining(key, headId);

      // NOTE: `resumeRequested` is consumed here, not in the caller, so an
      // explicit resume grants exactly one send past the current pause rather
      // than a standing override.
      if (queueState.resumeRequested[key]) {
        queueState.consumeResume(key);
      }

      const {parentId, parentTurnId} = resolveTurnParent(thread);
      const prompt = builtPromptFromStoreContent(head.content);
      void appendAndRunTurn(
        thread.id,
        prompt,
        parentId,
        head.runConfig.custom.agentConfigOverride ?? undefined,
        parentTurnId,
        head.runConfig.custom.permissions as TurnPermissionsPatch | undefined
      ).finally(() => {
        if (
          useQueueStore
            .getState()
            .queues[key]?.some(m => m.id === headId && m.draining)
        ) {
          useQueueStore.getState().clearDraining(key);
        }
      });
    };

    const maybeInitiateDrain = (thread: Thread) => {
      const key = queueKey(thread.id);
      const queueState = useQueueStore.getState();
      const head = queueState.queues[key]?.[0];
      if (!head || head.draining) {
        return;
      }

      // A summary-only thread's `status` is a placeholder ('idle'), not a
      // fetched run status; draining against it could send while the real
      // thread is still running. Wait for the authoritative load.
      //
      // TODO: Remove if the thread ever carries status for the active branch.
      if (thread.loadStatus !== 'loaded') {
        return;
      }

      const lastRunId = lastRunIds.get(thread.id);
      const action = computeNextAction({
        status: thread.status,
        pendingQuestions: thread.pendingQuestions != null,
        pendingNetworkAccessRequest: thread.pendingNetworkAccessRequest != null,
        resumeRequested: Boolean(queueState.resumeRequested[key]),
        readonly: isAgentChatReadonly(),
      });
      if (action !== 'drain' || !useQueueStore.isEligible(head.id)) {
        return;
      }

      // A drained run marks its id below so it never drains twice; the next
      // head waits for a new run to end (a new `lastRunId`). A run that instead
      // left the queue paused (a pending question or network request) returns
      // before that mark, so a later resume of the same run still drains.
      if (lastRunId && lastHandledRunIds.get(thread.id) === lastRunId) {
        return;
      }

      if (lastRunId) {
        lastHandledRunIds.set(thread.id, lastRunId);
      }

      initiateDrain(thread, key, head.id);
    };

    const evaluate = () => {
      const {threads, drafts} = store.getState();
      // NOTE: Include drafts, since a first turn that ends before
      // `turn-created` strands its queue on a still-unpromoted thread.
      const allThreads = {...drafts, ...threads};

      // Clean up stale run IDs.

      for (const id of lastRunIds.keys()) {
        if (!allThreads[id]) {
          lastRunIds.delete(id);
        }
      }
      for (const id of lastHandledRunIds.keys()) {
        if (!allThreads[id]) {
          lastHandledRunIds.delete(id);
        }
      }

      // Handle queue drains.

      for (const thread of Object.values(allThreads)) {
        if (thread.currentRunId) {
          lastRunIds.set(thread.id, thread.currentRunId);
        }
        maybeInitiateDrain(thread);
      }
    };

    evaluate();
    const unsubscribeThreads = store.subscribe(evaluate);
    const unsubscribeQueue = useQueueStore.subscribe(evaluate);
    return () => {
      unsubscribeThreads();
      unsubscribeQueue();
    };
  }, [store, appendAndRunTurn]);
}
